import React, { useEffect, useState } from "react";
import { getPostChecks, updatePostCheck } from "../../../api/Api";
import { showToast } from "../../../utils/toastUtils";
import { ProgressBar } from "../../../pages/Preview";
import CustomButton from "../../CustomButton";
import { WarningIcon } from "../../Icons/common";
import { FaCheckCircle } from "react-icons/fa";
import imgCheck from "../../../assets/checkStatusGood.png";
import styles from "./PostCheck.module.css";
import { useNavigate } from "react-router-dom";

// Post check component
export const PostCheck = ({ paperId }) => {
    const [data, setData] = useState(); // state to hold the data for post check cards
    // Initialize state to track checkbox statuses
    const [checkedIssues, setCheckedIssues] = useState({});
    const [openToast, setOpenToast] = useState(false);
    const navigate = useNavigate();
    
  
    // show toast message when checkbox is checked for 2 seconds and close
    useEffect(() => {
      if (openToast) {
        const timer = setTimeout(() => {
          setOpenToast(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }, [openToast]);
  
    // get post check issues from API
    useEffect(() => {
      const fetchPostCheckIssues = async () => {
        try {
          const response = await getPostChecks(paperId);
          if (response.status === 200) {
            setData(response.data);
            const checked = response.data.reduce((acc, issue) => {
              acc[issue.id] = issue.answer; // Set default checked state as false
              return acc;
            }, {});
            setCheckedIssues(checked);
          }
        } catch (error) {
          console.error("Error fetching post check issues:", error);
        }
      };
  
      fetchPostCheckIssues();
    }, [paperId]);
  
    // function to handle checkbox change
    const handleCheckChange = (issueId, checked) => {
      if (checked === true) {
        setOpenToast(true); // Show toast message when checkbox is checked
      }
      if (checked === "all") {
        setCheckedIssues((prevCheckedIssues) => {
          const updatedCheckedIssues = { ...prevCheckedIssues };
          Object.keys(updatedCheckedIssues).forEach((key) => {
            updatedCheckedIssues[key] = true; // Set all to true
          });
          return updatedCheckedIssues;
        });
  
        const payload = {
          question_id: "all",
          value: true,
        };
  
        // Update the checked state of all issues
        updatePostCheck(paperId, payload).then((response) => {
          console.log(response);
          if (response.status === 200) {
            showToast("All checks completed successfully", "success");
            // navigate to the previous page
            navigate(-1);
          } else {
            showToast(response.message || "Something went wrong", "error");
          }
        });
        return;
      }
  
      if (!issueId) return; // If no issueId is provided, do nothing
  
      const payload = {
        question_id: issueId,
        value: checked,
      };
  
      // Update the checked state of the specific issue
      updatePostCheck(paperId, payload).then((response) => {
        console.log(response);
        if (response.status === 200) {
          setCheckedIssues((prevCheckedIssues) => ({
            ...prevCheckedIssues,
            [issueId]: checked,
          }));
        } else {
          showToast(response.message || "Something went wrong", "error");
        }
      });
    };
  
    function getCompletionPercentage(checkedIssues) {
      const values = Object.values(checkedIssues);
      const total = values.length;
      const completed = values.filter((value) => value === true).length;
      const percentage = Math.floor((completed / total) * 100);
      return percentage;
    }

    const percentage = getCompletionPercentage(checkedIssues);

    return (
      <>
        <div className="d-flex justify-content-between gap-2 mb-3">
          <p className="p1 fw-bold mb-1">Manuscript Post-Checks </p>
          <div className="d-flex gap-2 align-items-center">
            <div className="text-nowrap fw-medium p3">{percentage}% Completed</div>
            <div style={{ width: "160px" }}>
              <ProgressBar progress={percentage} height={10} />
            </div>
          </div>
        </div>
        <p className="p2 mb-4 fw-normal text-secondary">
          These important checks can't be automated and need your manual
          verification. Complete them to ensure your manuscript meets all
          requirements.
        </p>
  
        <div className="bg-white secondary-box rounded-3 p-4">
          <IconAndText
            className="align-items-center"
            icon={<img src={imgCheck} alt="check-box-icon" width="25px" />}
            text={
              <div className="p1 fw-semibold">Manual Verification Required</div>
            }
          />
  
          <p className="p3 fw-normal">
            Please review and check off each item after verification
          </p>
  
          {data?.map((item, index) => (
            <PostCheckCard
              key={index}
              title={item?.title || item?.name}
              description={item.description}
              checked={checkedIssues?.[item.id] ?? item.checked} // fallback to item.checked if checkedIssues is undefined
              info={item.info}
              handleCheckChange={handleCheckChange}
              id={item.id}
            />
          ))}
  
          <div className="ps-3 d-flex justify-content-end align-items-center mt-4">
            <CustomButton className="px-2" onClick={() => handleCheckChange(null, "all")}>
              Complete All Checks
            </CustomButton>
          </div>
        </div>
  
        <IconAndText
          className="align-items-start mt-4 mb-2 opacity-50"
          icon={<WarningIcon size={20} />}
          text={
            <p className="p4 fw-semibold text-muted">
              These manual checks are important for ensuring your manuscript meets
              all requirements before submission. If you have questions about any
              item, click the info button for more details.
            </p>
          }
        />
  
        <CustomToast open={openToast} />
      </>
    );
  };



  const PostCheckCard = ({
    id,
    title,
    description,
    checked,
    info,
    handleCheckChange,
  }) => {
    return (
      <div className={`position-relative pe-2 ${checked ? "pt-2" : ""}`}>
        {checked && (
          <div
            className={`d-flex justify-content-center align-items-center position-absolute top-0 end-0 rounded-5 ${styles.checkCircle}`}
          >
            <FaCheckCircle size={20}/>
          </div>
        )}
  
        <div
          className={`primary-box rounded-4 py-3 px-4 d-flex align-content-start mb-3 w-100 ${
            checked ? styles.postCheckCard : ""
          }`}
        >
          <input
            type="checkbox"
            className={`${styles.checkbox} form-check-input rounded-5 mt-1 me-2`}
            checked={checked}
            onChange={() => handleCheckChange(id, !checked)}
          />
          <div className="w-100">
            <div className="d-flex flex-column w-100">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <div
                  onClick={() => handleCheckChange(id, !checked)}
                  style={{ cursor: "pointer" }}
                  className={`p3 fw-semibold ${
                    checked ? styles.postCheckTitle : ""
                  }`}
                >
                  {title}
                </div>
                {/* info icon */}
                {/* <div>
                  <WarningIcon color="#808080" size={20} className="w-auto"/> 
                </div> */}
              </div>
              <p
                className={`p3 fw-normal text-secondary text-wrap ${
                  checked ? styles.postCheckTitle : ""
                } mb-3`}
                style={{overflowWrap:"anywhere"}}
              >
                {description}
              </p>
            </div>
            {info && (
              <div className="primary-box rounded-4 p-3">
                <IconAndText
                  icon={info?.icon}
                  text={info?.text}
                  className="align-items-start mb-0"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const CustomToast = ({ message, open, close }) => {
    if (!open) return null;
    const display = open ? "flex" : "none";
    return (
      <div
        style={{
          border: "1px solid var(--blue)",
          right: "50px",
          bottom: "100px",
          display: display,
        }}
        className=" flex-column justify-content-center p-3 rounded-4 bg-white position-fixed transition-all duration-300"
      >
        <p className="p3 mb-1">Item verified</p>
        <p className="p4 mb-0">
          Great progress! Keep going with the verification.
        </p>
      </div>
    );
  };


  const IconAndText = ({ icon, text, className }) => {
    return (
      <div className={`d-flex gap-2 mb-2 ${className}`}>
        <div className="w-auto">{icon}</div>
        {text}
      </div>
    );
  };
  